import type { CompareMethodKeyEnum } from "src/types/enum/CompareMethodKey";
import type { CompareRangeType } from "src/types/enum/CompareRangeType";
import type { DayOfTheWeek } from "src/types/enum/DayOfTheWeek";
import type { LanguageEnum } from "src/types/enum/LanguageEnum";
import {
    NewsletterComparePeriodKeyEnum,
    OperationalOptionKeyEnum,
    type UserPreferenceKeyEnum,
} from "src/types/enum/UserPreferenceKeyEnum";
import type { PerformanceCompareConfigKey } from "src/types/performance/PerformanceCompareConfigKey";
import type { UserRoleEnum } from "src/types/role";
import type { ReportModeEnum, SectionListEnum } from "src/views/account/PreferencesView/config";
import type { Channel } from "./channel";
import type { Id } from "./id";
import type { Organization } from "./organization";
import type { Pos } from "./pos";
import type { ThirdServiceCategory } from "./posCredential";
import type { PosGroup } from "./posGroup";

export enum UserStatus {
    activated = "activated",
    archived = "archived",
}

export enum UserKeys {
    id = "id",
    email = "email",
    firstName = "firstName",
    lastName = "lastName",
    password = "password",
    poss = "poss",
    posGroups = "posGroups",
    organization = "organization",
    role = "role",
    roleSuperAdmin = "role",
    user = "user",
    status = "status",
}

export interface UserBase {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    posGroups?: PosGroup[];
    organization?: Organization;
    role: UserRoleEnum;
    status: UserStatus;
    createdDateUTC: Date;
    channels?: Channel[];
    services?: ThirdServiceCategory[];
    timezone: string;
    hashIntercom: string;
    organizationId?: number;
}

export interface User extends UserBase {
    poss?: Pos[];
}

export interface UserRelationIds extends UserBase {
    poss?: Id[];
}

export interface UserLanguagePreferenceKeyMetadata {
    language: LanguageEnum;
}

export interface UserNewsletterPreferenceKeyMetaData {
    [NewsletterComparePeriodKeyEnum.DASHBOARD_COMPARE_PERIOD_KEY]?: CompareRangeType;
    [NewsletterComparePeriodKeyEnum.ANALYZE_COMPARE_PERIOD_KEY]?: PerformanceCompareConfigKey;
    enableMinMaxTable?: boolean;
    enableSlotsTrend?: boolean;
    sendingDow?: DayOfTheWeek;
    isActive: boolean;
}

export interface UserOperationalActivityReportPreferenceKeyMetaData {
    [OperationalOptionKeyEnum.MAIN_COMPARE_PERIOD_KEY]?: CompareRangeType;
    [OperationalOptionKeyEnum.OPERATIONAL_COMPARE_PERIOD_KEY]?: CompareRangeType;
    [OperationalOptionKeyEnum.COMPARE_METHOD]?: CompareMethodKeyEnum;
    [OperationalOptionKeyEnum.REPORT_MODE_LIST]?: ReportModeEnum[];
    [OperationalOptionKeyEnum.POS_SECTION_LIST]?: SectionListEnum[];
    [OperationalOptionKeyEnum.GROUP_SECTION_LIST]?: SectionListEnum[];
    [OperationalOptionKeyEnum.ORGANIZATION_SECTION_LIST]?: SectionListEnum[];
    sendingDow?: DayOfTheWeek;
    isActive: boolean;
}

export interface UserErrorReportPreferenceKeyMetaData {
    sendingDow?: DayOfTheWeek;
    isActive: boolean;
}

export type UserPreferenceKeyMetaData =
    | UserNewsletterPreferenceKeyMetaData
    | UserLanguagePreferenceKeyMetadata
    | UserOperationalActivityReportPreferenceKeyMetaData
    | UserErrorReportPreferenceKeyMetaData
    | unknown;

export interface UserPreferenceKey<T extends UserPreferenceKeyMetaData = unknown> {
    id: number;
    userId: number;
    preferenceKey: UserPreferenceKeyEnum;
    metaData: T;
    updatedDateUTC: Date;
}

export type UserPreferenceKeyCreate<T extends UserPreferenceKeyMetaData = unknown> = Pick<
    UserPreferenceKey<T>,
    "preferenceKey" | "metaData"
>;
